import React, { useState, useEffect } from 'react';
import Draggable, { DraggableEvent } from 'react-draggable';
import Split from "react-split";
import styled from 'styled-components';
import linq from 'linq';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { List, ListItem, ListItemText, ListItemIcon } from '@material-ui/core';
import { Box, Button, Paper, IconButton, TextField, Fab, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { SheetKinds } from '../redux/sheetDbSlice';
import { PaletteDb, updatePalette } from '../redux/paletteDbSlice';
import { CardKinds, CardTemplateDb, generateCardTemplateId, addCardTemplate } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDb, addCardTemplateAttribute } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDb, addCardTemplateShape } from '../redux/cardTemplateShapeDbSlice';
import { CardBaseProps, StoryCard, MapCard, CardDb, CardAddPayload, generateCardId, isStoryCard, isCommonCard, isMemoCard, isUiCard, isDrawCard, updateCard } from '../redux/cardDbSlice';
import { CardAttributeDb } from '../redux/cardAttributeDbSlice';
import { CardShapeDb } from '../redux/cardShapeDbSlice';
import { MemoCardContentDb } from '../redux/memoCardContentDbSlice';
import { DrawCardContentDb } from '../redux/drawCardContentDbSlice';
import { ArrowDb, addArrow, updateArrow, deleteArrow } from '../redux/arrowDbSlice';
import { updateLatelyCardTemplate } from '../redux/latelyCardTemplateDbSlice';
import { RelationUserPaletteDb, updateRelationUserPalette } from '../redux/relationUserPaletteDbSlice';
import { addRelationUserCardTemplate } from '../redux/relationUserCardTemplateDbSlice';
import { RelationPaletteCardTemplateDb, addRelationPaletteCardTemplate, updateRelationPaletteCardTemplate } from '../redux/relationPaletteCardTemplateDbSlice';
import { updateUserCondition } from '../redux/userConditionSlice';
import useUserCondition from '../hooks/useUserCondition';

import DraggableSort from '../general/DraggableSort';
import { initial } from '../card_components';
import { getCardTemplate } from '../card_components/templateIndex';
import { getAttributeInitial, AddCardValueProps, addCardValue } from '../card_components/index';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useCurrentDb from '../hooks/useCurrentDb';
import useElementDimensions from '../hooks/useElementDimensions';
import useAllPaletteDbs from '../hooks/useAllPaletteDbs';
import useLatelyCardTemplates from '../hooks/useLatelyCardTemplates';
import useCardTemplateIdsInPalette from '../hooks/useCardTemplateIdsInPalette';
import useArrowDbsByCard from '../hooks/useArrowDbsByCard';
import useOneArrowDb from '../hooks/useOneArrowDb';
import useOneCardDb from '../hooks/useOneCardDb';
import { CoordinateType, deepCopy, makeLately } from '../general';
import EditTemplateDialog from '../components/EditTemplateDialog';
import DeleteTemplateDialog from '../components/DeleteTemplateDialog';
import AddPaletteDialog from './AddPaletteDialog';
import DeletePaletteDialog from '../components/DeletePaletteDialog';
import PublishMenu, { PublishMenuAnchorType } from '../shop_components/PublishMenu';
import PublishDialog from '../shop_components/PublishDialog';

type FadeDialogTitleProps = {
  $isVisible: boolean;
}
const FadeDialogTitle = styled(DialogTitle)<FadeDialogTitleProps>`
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 200ms ease-out;
`
type AddCardDialogActionsProps = {
  $isVisible: boolean;
}
const AddCardDialogActions = styled(DialogActions)<AddCardDialogActionsProps>`
  position: absolute;
  right: 0;
  bottom: 0;
  opacity: ${props => props.$isVisible ? 1 : 0};
  pointer-events: ${props => props.$isVisible ? undefined : 'none'};
  transition: opacity 180ms;
`
const EditButton = styled(Button)`
  position: absolute;
  right: 8px;
  top: 8px;
`
type LeftPanelProps = {
  $width: string;
}
const LeftPanel = styled.div<LeftPanelProps>`
  position: relative;
  width: ${props => props.$width};
  cursor: auto;
  z-index: 1;
`
type RightPanelProps = {
  $width: string;
  $cardDragging: boolean;
}
const RightPanel = styled.div<RightPanelProps>`
  position: relative;
  width: ${props => props.$width};
  height: 100%;
  cursor: auto;
  z-index: ${props => props.$cardDragging ? 2 : 0};
  overflow: hidden;
`
type CardsGridListWrapperProps = {
  $height?: number;
  elementDragging: boolean;
}
const CardsGridListWrapper = styled.div<CardsGridListWrapperProps>`
  position: relative;
  top: 0;
  width: 100%;
  height: ${props => props.$height ? `${props.$height}px` : '100%'};
  overflow-x: hidden;
  overflow-y: auto;
  z-index: ${props => props.elementDragging ? 2 : 0};
  transition: height 300ms;
`
type PaletteListItemProps = {
  $dragging: boolean;
  $invalidTransition: boolean;
}
const PaletteListItem = styled(ListItem)<PaletteListItemProps>`
  position: absolute;
  height: 72px;
  pointer-events: ${props => props.$dragging ? 'none' : undefined};
  z-index: ${props => props.$dragging ? 2 : 1};
  transition: ${props => (props.$dragging || props.$invalidTransition) ? undefined : '200ms ease-out'};
`
const GreyListItemIcon = styled(ListItemIcon)`
  color: #9e9e9e;
`
type PaletteNameWrapperProps = {
  $editMode: boolean;
}
const PaletteNameWrapper = styled.div<PaletteNameWrapperProps>`
  position: sticky;
  margin-top: ${props => props.$editMode ? '0' : '-72px'};
  width: 64%;
  height: auto;
  background-color: white;
  opacity: ${props => props.$editMode ? 1 : 0};
  z-index: 3;
  transition: 300ms;
`
const PaletteNameTextField = styled(TextField)`
  width: 100%;
  margin: 8px 0 8px 8px;
`
type TileWrapperProps = {
  $dragging: boolean;
  $invalidTransition: boolean;
  paletteChanging: boolean;
}
const TileWrapper = styled.div<TileWrapperProps>`
  position: absolute;
  width: 200px;
  height: 190px;
  pointer-events: ${props => props.$dragging ? 'none' : 'auto'};
  z-index: ${props => props.$dragging ? 5 : 4};
  transition: ${props => (props.$dragging || props.$invalidTransition) ? undefined : '300ms ease-out'};
`
type TileDivProps = {
  $invalidTransition: boolean;
  paletteChanging: boolean;
}
const TileDiv = styled.div<TileDivProps>`
  opacity: ${props => props.paletteChanging ? '0.5' : undefined};
  transform: ${props => props.paletteChanging ? 'scale(0.7)' : undefined};
  transition: ${props => props.$invalidTransition ? undefined : '100ms ease-in'};
`
type CardBasePaperProps = {
  $invalidTransition: boolean;
}
const CardBasePaper = styled(Paper)<CardBasePaperProps>`
  width: 200px;
  height: 120px;
  cursor: grab;
  transition: ${props => props.$invalidTransition ? 'none' : undefined};
`
const CardNameText = styled.div`
  text-align: center;
  overflow: hidden;
  word-break: keep-all;
`
type EditMenuBoxProps = {
  $editMode: boolean;
  $dragging: boolean;
  $invalidTransition: boolean;
}
const EditMenuBox = styled(Box)<EditMenuBoxProps>`
  opacity: ${props => (props.$dragging || !props.$editMode) ? 0 : 1};
  pointer-events: ${props => props.$editMode ? undefined : 'none'};
  transition: ${props => props.$invalidTransition ? undefined : 'opacity 300ms'};
`
type MakeTemplateFabProps = {
  $isVisible: boolean;
}
const MakeTemplateFab = styled(Fab)<MakeTemplateFabProps>`
  position: absolute;
  bottom: 8vh;
  right: 3vw;
  color: white;
  z-index: 999;
  background-color: #1e88e5;
  transform: ${props => props.$isVisible ? 'scale(1)' : 'scale(0)'};
  transition: transform 180ms;
  &:hover {
    background-color: #1976d2;
  }
`

const isStoryRootCard = (card: CardDb): card is StoryCard => {
  return card.sheetKind === 'story' && !card.parentId;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blue: {
      color: '#1e88e5',
    },
    grey: {
      color: '#9e9e9e',
    },
    split: {
      display: 'flex',
      height: '74vh',
      cursor: 'ew-resize',
      zIndex: 6,
      position: 'relative',
    },
  }),
);

type PaletteValueType = PaletteDb & { order: number }
export type TemplateValueType = CardTemplateDb & { order: number }
export type TemplateListHashType = { [key in string]: { ids: string[], values: TemplateValueType[] } }
const paletteListItemHeight = 72;
const colWidth = initial.cardWidth + 2 + 16 * 2;// カードの幅＋左右マージン
const colHeight = initial.cardHeight + 2 + 24 + 44 + 8 * 2;// カードの高さ＋枠の太さ（1*2）＋カード名の高さ＋ボタンの高さ＋上下マージン
const dragTargetPaletteClass = 'palette-list-item';
const dragHandlePaletteClass = 'palette-drag-indicator';
const dragTargetCTClass = 'draggable-tile';
const dragHandleCTClass = 'handle-card';

type SimpleListProps = {
  editMode: boolean;
  paletteIds: string[];
  paletteList: PaletteValueType[];
  setPaletteList: (list: PaletteValueType[]) => void;
  onDelete?: (id: string) => void;
  selectingId: string;
  onSelect: (id: string) => void;
  onAddPalette: () => void;
  draggingId?: string;
  setDraggingId: (id?: string) => void;
  changePOrder: () => void;
  setInvalidCardTransition: (value: boolean) => void;
}
const SimpleList: React.FC<SimpleListProps> = (props) => {
  const { editMode, paletteIds, paletteList, setPaletteList, onDelete, selectingId, onSelect, onAddPalette, draggingId, setDraggingId, changePOrder, setInvalidCardTransition } = props;
  const classes = useStyles();
  const orderAdjust = editMode ? 0 : 1;
  const [invalidPaletteTransition, setInvalidPaletteTransition] = useState(false);

  const onStop = () => {
    setInvalidPaletteTransition(true);
    setTimeout(() => setInvalidPaletteTransition(false), 300)
    setInvalidCardTransition(true);
    setTimeout(() => setInvalidCardTransition(false), 300)
    changePOrder();
  }

  return (
    <Box style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', overflowY: 'auto' }}>
      <List component="nav" style={{ width: 'inherit', height: (paletteIds.length + 1) * paletteListItemHeight }}>
        { editMode ||
          <Draggable disabled position={{x: 0, y: 0}}>
            <PaletteListItem button key={'lately'} onClick={() => onSelect('lately')} selected={selectingId === 'lately'}
              $dragging={false} $invalidTransition={invalidPaletteTransition}
            >
              <ListItemText primary="最近使ったカード" />
            </PaletteListItem>
          </Draggable>
        }
        { paletteIds.map((id, ix) => {
          const palette = paletteList[ix];
          if (!palette) return undefined;
          return (
            <DraggableSort key={ix}
              draggableProps={{
                position: { x: 0, y: (palette.order + orderAdjust) * paletteListItemHeight },
                handle: `.${dragHandlePaletteClass}`,
                onStart: () => onSelect(id),
                onStop: () => onStop(),
                bounds: 'parent',
                axis: 'y',
                disabled: !editMode
              }}
              targetClass={dragTargetPaletteClass}
              id={id} ids={paletteIds} values={paletteList}
              setValues={setPaletteList}
              setDraggingId={setDraggingId}
            >
              <PaletteListItem button selected={selectingId === id}
                onClick={() => onSelect(id)} key={id}
                id={id} className={dragTargetPaletteClass}
                $dragging={id === draggingId}
                $invalidTransition={invalidPaletteTransition}
              >
                { editMode && <GreyListItemIcon className={dragHandlePaletteClass}><DragIndicatorIcon /></GreyListItemIcon> }
                <ListItemText primary={palette.name} />
                { editMode &&
                  <ListItemIcon>
                    <IconButton onClick={() => onDelete && onDelete(id)}
                      disabled={palette.standard}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </ListItemIcon>
                }
              </PaletteListItem>
            </DraggableSort>
          );
        }) }
        { editMode &&
          <Draggable disabled position={{x: 0, y: paletteIds.length * paletteListItemHeight}}>
            <PaletteListItem button key={'add'} onClick={() => onAddPalette()}
              $dragging={false} $invalidTransition={invalidPaletteTransition}
            >
              <ListItemIcon className={classes.blue}><AddIcon /></ListItemIcon>
              <ListItemText primary="パレットを作成する" className={classes.blue} />
            </PaletteListItem>
          </Draggable>
        }
      </List>
    </Box>
  );
}

type CardsGridListProps = {
  editMode: boolean;
  paletteId: string;
  selectingCardId: string | undefined;
  cardClicked: (id: string) => void;
  cardDoubleClicked: () => void;
  ids: string[];
  values: TemplateValueType[];
  onEdit: (id: string | undefined) => void;
  onDelete: (paletteId?: string, cardTemplateId?: string) => void;
  onDuplicate: (id: string) => void;
  draggingId?: string;
  setDraggingId: (id?: string) => void;
  paletteChangingId?: string;
  changeCTOrder: (ev: DraggableEvent, id: string) => void;
  setTemplateValues: (values: TemplateValueType[]) => void;
  checkPaletteChanging: (ev: DraggableEvent, id: string) => void;
  invalidCardTransition: boolean;
  invalidEditBoxTransition: boolean;
  width?: number;
  height?: number;
  setMenuAnchor: (value: PublishMenuAnchorType) => void;
}
export const CardsGridList: React.FC<CardsGridListProps> = ({ editMode, paletteId, selectingCardId, cardClicked, cardDoubleClicked, ids, values, onEdit, onDelete, onDuplicate, draggingId, setDraggingId, paletteChangingId, changeCTOrder, setTemplateValues, checkPaletteChanging, invalidCardTransition, invalidEditBoxTransition, width, height, setMenuAnchor }) => {
  const [latelyIds, latelyValues] = useLatelyCardTemplates();
  const templateIds = (!editMode && paletteId === 'lately') ? latelyIds : ids;
  const templateValues = (!editMode && paletteId === 'lately') ? latelyValues : values;
  const [listCols, setListCols] = useState<number>(3);

  useEffect(() => {
    if (width) setListCols(Math.trunc(width / colWidth));
  }, [width])

  return (
    <CardsGridListWrapper $height={height} elementDragging={Boolean(draggingId)}>
      <div style={{ paddingTop: '8px', paddingLeft: '16px', height: colHeight * Math.trunc(templateIds.length / listCols) }}>
        { templateIds.map((id, ix) => {
          const cardTemplate = templateValues[ix];
          if (!cardTemplate) return undefined;
          return (
            <DraggableSort key={ix}
              draggableProps={{
                position: { x: colWidth * (cardTemplate.order % listCols), y: colHeight * Math.trunc(cardTemplate.order / listCols) },
                handle: `.${dragHandleCTClass}`,
                onStop: (ev: DraggableEvent) => changeCTOrder(ev, id),
                bounds: { left: undefined, top: undefined, right: undefined, bottom: undefined },
                disabled: !editMode
              }}
              targetClass={dragTargetCTClass}
              onDrag={(ev: DraggableEvent) => checkPaletteChanging(ev, id)}
              id={id} ids={ids} values={values}
              setValues={setTemplateValues}
              setDraggingId={setDraggingId}
            >
              <TileWrapper id={id} className={dragTargetCTClass}
                $dragging={id === draggingId} $invalidTransition={invalidCardTransition}
                paletteChanging={id === paletteChangingId}
              >
                <TileDiv $invalidTransition={invalidCardTransition} paletteChanging={id === paletteChangingId}>
                  <Tooltip title={(cardTemplate.notes) ? <div style={{ whiteSpace: 'break-spaces' }}>{cardTemplate.notes}</div> : ''} arrow>
                    <div>
                      <CardBasePaper id={id} className={dragHandleCTClass}
                        elevation={id === draggingId ? 8 : 0}
                        onClick={() => !editMode ? cardClicked(id) : undefined}
                        onDoubleClick={() => !editMode ? cardDoubleClicked() : cardTemplate.kind === 'common' ? onEdit(id) : undefined}
                        onContextMenu={ev => setMenuAnchor({ open: true, anchorPosition: { left: ev.pageX, top: ev.pageY }, id })}
                        $invalidTransition={invalidCardTransition}
                      >
                        { getCardTemplate(cardTemplate.kind, id, id === selectingCardId) }
                      </CardBasePaper>
                      <CardNameText>{ cardTemplate.name }</CardNameText>
                    </div>
                  </Tooltip>
                  <EditMenuBox position="relative" left="80px"
                    $editMode={editMode} $dragging={id === draggingId}
                    $invalidTransition={invalidEditBoxTransition}
                  >
                    <IconButton onClick={() => onDuplicate(id)} disabled={cardTemplate.kind !== 'common'}>
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => onEdit(id)} disabled={cardTemplate.kind !== 'common'}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(paletteId, id)} disabled={cardTemplate.kind !== 'common'}>
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  </EditMenuBox>
                </TileDiv>
              </TileWrapper>
            </DraggableSort>
          );
        }) }
      </div>
    </CardsGridListWrapper>
  );
}

type AddCardDialogContentProps = {
  open: boolean;
  editMode: boolean;
  paletteIds: string[];
  palettes: PaletteDb[];
  selectingPaletteId: string;
  paletteSelect: (id: string) => void;
  selectingCardId: string | undefined;
  setSelectingCardId: (id: string | undefined) => void;
  getCardKind: (id: string) => CardKinds;
  getCardTemplateItems: (cardTemplateId: string) => [string[], CardTemplateAttributeDb[], string[], CardTemplateShapeDb[]];
  dicision: () => void;
  invalidCardTransition: boolean;
  setInvalidCardTransition: (value: boolean) => void;
  invalidEditBoxTransition: boolean;
}
const AddCardDialogContent: React.FC<AddCardDialogContentProps> = ({open, editMode, paletteIds, palettes, selectingPaletteId, selectingCardId, setSelectingCardId, paletteSelect, getCardKind, getCardTemplateItems, dicision, invalidCardTransition, setInvalidCardTransition, invalidEditBoxTransition}) => {
  const dispatch = useDispatch();
  const paletteDb = useSelector((state: RootState) => state.paletteDb);
  const cardTemplateDb = useSelector((state: RootState) => state.cardTemplateDb);
  const relationUserCardTemplates = useSelector((state: RootState) => state.relationUserCardTemplateDb).relationUserCardTemplates;
  const relationUserPalettes = useSelector((state: RootState) => state.relationUserPaletteDb).relationUserPalettes;
  const relationPaletteCardTemplates = useSelector((state: RootState) => state.relationPaletteCardTemplateDb).relationPaletteCardTemplates;
  const userId = useCurrentUserId();
  const cardTemplatesLength = useCardTemplateIdsInPalette(selectingPaletteId).length;
  const [userConditionId, userCondition] = useUserCondition(userId);

  const classes = useStyles();

  const [paletteList, setPaletteList] = useState<PaletteValueType[]>([]);
  const [templateListHash, setTemplateListHash] = useState<TemplateListHashType>({});
  const [selectingPalette, setSelectingPalette] = useState<PaletteDb>();
  const [paletteName, setPaletteName] = useState<string>('');
  const [draggingId, setDraggingId] = useState<string>();
  const [paletteChangingId, setPaletteChangingId] = useState<string>();
  const [splitRatios, setSplitRatios] = useState<number[]>([25,75]);
  const [splitUpdated, setSplitUpdated] = useState(true);
  const rightPanelId = 'card-browser-right-panel';
  const { width: rightPanelWidth, height: rightPanelHeight } = useElementDimensions(rightPanelId, splitUpdated);

  type DialogActionType = { show: boolean, id: string | undefined }
  const [editTemplateAction, setEditTemplateAction] = 
    useState<DialogActionType>({ show: false, id: undefined });
  const [addPaletteAction, setAddPaletteAction] = useState<boolean>(false);
  const [deleteCategoryAction, setDeleteCategoryAction] = useState<DialogActionType>({show: false, id: undefined});
  type DeleteTemplateActionType = { show: boolean, paletteId?: string, cardTemplateId?: string };
  const [deleteTemplateAction, setDeleteTemplateAction] = useState<DeleteTemplateActionType>({ show: false });

  const [menuAnchor, setMenuAnchor] = useState<PublishMenuAnchorType>();
  const [publishAction, setPublishAction] = useState<DialogActionType>({ show: false, id: undefined });

  useEffect(() => {
    // set paletteList
    const newPaletteList: PaletteValueType[] = [];
    paletteIds.forEach((id, ix) => {
      const palette = palettes[ix];
      const [rupId, rup] = getRup(id);
      if (!rupId || !rup) return;
      newPaletteList.push({ ...palette, order: rup.order });
    })
    setPaletteList(newPaletteList);

    // set templateListHash
    const newTemplateList = deepCopy(templateListHash);
    paletteIds.forEach(id => {
      const rpcIds = Object.keys(relationPaletteCardTemplates).filter(rpcId => relationPaletteCardTemplates[rpcId].paletteId === id);
      rpcIds.sort((a, b) => {
        const rpcA = relationPaletteCardTemplates[a];
        const rpcB = relationPaletteCardTemplates[b];
        return rpcA.order - rpcB.order;
      })
      const templateIds = rpcIds.map(rpcId => relationPaletteCardTemplates[rpcId].cardTemplateId);
      const templates = templateIds.map((templateId, templateIx) => {
        return { ...cardTemplateDb.cardTemplates[templateId], order: relationPaletteCardTemplates[rpcIds[templateIx]].order };
      });
      newTemplateList[id] = { ids: templateIds, values: templates };
    })
    setTemplateListHash(newTemplateList);

    // set selecting palette
    const paletteIx = paletteIds.indexOf(selectingPaletteId);
    if (paletteIx !== -1) {
      setSelectingPalette(palettes[paletteIx]);
      setPaletteName(palettes[paletteIx].name);
    } else {
      setSelectingPalette(undefined);
      setPaletteName('');
    }

    // set splitRatios
    if (!userCondition) return;
    setSplitRatios(userCondition.splitRatios.cardBrowser);
  }, [cardTemplateDb, relationUserPalettes, selectingPaletteId, userCondition])

  const changePaletteName = (ev: any) => {
    const newName = ev.target.value;
    setPaletteName(newName);
    dispatch(updatePalette({id: selectingPaletteId, name: newName}));
  }
  const duplicateTemplate = (templateId: string) => {
    const templateValue = cardTemplateDb.cardTemplates[templateId];
    if (!templateValue) return;
    const [, attributes, , shapes] = getCardTemplateItems(templateId);
    const rucIds = Object.keys(relationUserCardTemplates).filter(id => relationUserCardTemplates[id].cardTemplateId === templateId);
    const rucValues = rucIds.map(id => relationUserCardTemplates[id]);
    const rpcIds = Object.keys(relationPaletteCardTemplates).filter(id => relationPaletteCardTemplates[id].cardTemplateId === templateId);
    const rpcValues = rpcIds.map(id => relationPaletteCardTemplates[id]);
    const newId = generateCardTemplateId();
    dispatch(addCardTemplate({...templateValue, id: newId, name: templateValue.name + 'のコピー'}));
    rucValues.forEach(value => dispatch(addRelationUserCardTemplate({...value, cardTemplateId: newId})));
    rpcValues.forEach(value => dispatch(addRelationPaletteCardTemplate({...value, cardTemplateId: newId, order: cardTemplatesLength})))
    attributes.forEach(value => dispatch(addCardTemplateAttribute({...value, cardTemplateId: newId})))
    shapes.forEach(value => dispatch(addCardTemplateShape({...value, cardTemplateId: newId})))
  }
  const getRup = (paletteId: string): [string, RelationUserPaletteDb] => {
    const id = Object.keys(relationUserPalettes).filter(id => {
      const target = relationUserPalettes[id];
      return target.paletteId === paletteId && target.userId === userId;
    })[0];
    const value = relationUserPalettes[id];
    return [id, value];
  }
  const getRpc = (paletteId: string, templateId: string): [string, RelationPaletteCardTemplateDb] => {
    const id = Object.keys(relationPaletteCardTemplates).filter(id => {
      const target = relationPaletteCardTemplates[id];
      return target.paletteId === paletteId && target.cardTemplateId === templateId;
    })[0];
    const value = relationPaletteCardTemplates[id];
    return [id, value];
  }
  const getPaletteId = (element: HTMLElement): string | undefined => {
    const elementClass = element.className;
    if (!element || typeof elementClass !== 'string') return;
    else if (elementClass.includes(dragTargetPaletteClass)) return element.id;
    else if (element.parentElement) return getPaletteId(element.parentElement);
    else return;
  }
  const isStandardPalette = (paletteId: string) => {
    const palette = paletteDb.palettes[paletteId];
    return palette?.standard;
  }
  const changePOrder = () => {
    const ids = paletteIds;
    const list = paletteList;

    ids.forEach((targetId, targetIx) => {
      const value = list[targetIx];
      const [rupId, rup] = getRup(targetId);
      if (value.order === rup.order) return;
      dispatch(updateRelationUserPalette({ id: rupId, order: value.order }));
    })
  }
  const changeCTOrder = (ev: DraggableEvent, id: string) => {
    setPaletteChangingId(undefined);

    const target = ev.target as HTMLElement;
    const destinationPaletteId = getPaletteId(target);

    const ids = templateListHash[selectingPaletteId].ids;
    const list = templateListHash[selectingPaletteId].values;

    const [rpcId, rpc] = getRpc(selectingPaletteId, id);
    if (destinationPaletteId && destinationPaletteId !== rpc.paletteId && !isStandardPalette(destinationPaletteId) && !isStandardPalette(rpc.paletteId)) {
      const destination = templateListHash[destinationPaletteId];
      ids.forEach(targetId => {
        const [targetRpcId, targetRpc] = getRpc(selectingPaletteId, targetId);
        if (targetId === id) return;
        else if (targetRpc.order > rpc.order) dispatch(updateRelationPaletteCardTemplate({ id: targetRpcId, order: targetRpc.order - 1 }));
      })
      dispatch(updateRelationPaletteCardTemplate({ id: rpcId, paletteId: destinationPaletteId, order: destination.ids.length }));

      paletteSelect(destinationPaletteId);
    } else {
      ids.forEach((targetId, targetIx) => {
        const value = list[targetIx];
        const [rpcId, rpc] = getRpc(selectingPaletteId, targetId);
        if (value.order === rpc.order) return;
        dispatch(updateRelationPaletteCardTemplate({ id: rpcId, order: value.order }));
      })
    }
  }
  const setTemplateValues = (values: TemplateValueType[]) => {
    const newLists = deepCopy(templateListHash);
    newLists[selectingPaletteId].values = values;
    setTemplateListHash(newLists);
  }
  const checkPaletteChanging = (ev: DraggableEvent, id: string) => {
    const target = ev.target as HTMLElement;
    const [, rpc] = getRpc(selectingPaletteId, id);
    const destinationPaletteId = getPaletteId(target);
    if (destinationPaletteId && destinationPaletteId !== rpc.paletteId && !isStandardPalette(destinationPaletteId) && !isStandardPalette(rpc.paletteId)) setPaletteChangingId(id);
    else setPaletteChangingId(undefined);
  }
  const updatePOrder = (PaletteId: string) => {
    if (!selectingPaletteId) return;
    const [targetId, target] = getRup(PaletteId);
    const ids = paletteIds;
    ids.forEach(id => {
      const [rupId, rup] = getRup(id);
      if (rupId === targetId) return;
      else if (rup.order > target.order) dispatch(updateRelationUserPalette({ id: rupId, order: rup.order - 1 }));
    })
  }
  const updateCTOrder = (templateId: string) => {
    if (!selectingPaletteId) return;
    const [targetId, target] = getRpc(selectingPaletteId, templateId);
    const templates = templateListHash[selectingPaletteId];
    templates.ids.forEach(id => {
      const [rpcId, rpc] = getRpc(selectingPaletteId, id);
      if (rpcId === targetId) return;
      else if (rpc.order > target.order) dispatch(updateRelationPaletteCardTemplate({ id: rpcId, order: rpc.order - 1 }));
    })
  }
  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition) return;
    setSplitUpdated(false);
    const newRatio = deepCopy(userCondition.splitRatios);
    newRatio.cardBrowser = sizes;
    dispatch(updateUserCondition({ id: userConditionId, splitRatios: newRatio }));
    setSplitRatios(newRatio.cardBrowser);
    setTimeout(() => setSplitUpdated(true), 1)
  }

  return (
    <DialogContent>
      <div style={{width: '100%', height: '74vh', marginBottom: 58.25}}>
        <Split
            minSize={250}
            expandToMin={false}
            gutterSize={5}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
            sizes={[Number.isFinite(splitRatios[0]) ? splitRatios[0] : 25, Number.isFinite(splitRatios[1]) ? splitRatios[1] : 75]}
            onDrag={(sizes: number[]) => dragSplit(sizes)}
            className={classes.split}
        >
          <LeftPanel $width={`${splitRatios[0]}%`}>
            <SimpleList editMode={editMode}
              paletteIds={paletteIds} paletteList={paletteList}
              setPaletteList={setPaletteList}
              onDelete={(id) => setDeleteCategoryAction({show: true, id: id})}
              selectingId={selectingPaletteId} onSelect={(id: string) => paletteSelect(id)}
              onAddPalette={() => setAddPaletteAction(true)}
              draggingId={draggingId} setDraggingId={setDraggingId}
              changePOrder={changePOrder}
              setInvalidCardTransition={setInvalidCardTransition}
            />
          </LeftPanel>
          <RightPanel id={rightPanelId} $width={`${splitRatios[1]}%`} $cardDragging={draggingId !== undefined && draggingId.substring(0, 3) === 'ct:'}>
            { (selectingPalette) &&
              <PaletteNameWrapper $editMode={editMode}>
                <PaletteNameTextField label="パレット名" variant="outlined" value={paletteName}
                  onChange={changePaletteName}
                  disabled={selectingPalette.standard || !editMode}
                />
              </PaletteNameWrapper>
            }
            <CardsGridList editMode={editMode}
              paletteId={selectingPaletteId}
              selectingCardId={selectingCardId}
              cardClicked={(id: string) => setSelectingCardId(id)}
              cardDoubleClicked={() => dicision()}
              ids={templateListHash[selectingPaletteId] ? templateListHash[selectingPaletteId].ids : []}
              values={templateListHash[selectingPaletteId] ? templateListHash[selectingPaletteId].values : []}
              onEdit={(id) => setEditTemplateAction({show: true, id: id})}
              onDelete={(paletteId, cardTemplateId) => setDeleteTemplateAction({show: true, paletteId: paletteId, cardTemplateId: cardTemplateId})}
              onDuplicate={(id: string) => duplicateTemplate(id)}
              draggingId={draggingId} setDraggingId={setDraggingId}
              paletteChangingId={paletteChangingId}
              changeCTOrder={changeCTOrder}
              setTemplateValues={setTemplateValues}
              checkPaletteChanging={checkPaletteChanging}
              invalidCardTransition={invalidCardTransition}
              invalidEditBoxTransition={invalidEditBoxTransition}
              width={rightPanelWidth} height={(editMode && rightPanelHeight) ? rightPanelHeight - 72 : rightPanelHeight}
              setMenuAnchor={setMenuAnchor}
              />
          </RightPanel>
        </Split>
        <MakeTemplateFab onClick={() => setEditTemplateAction({show: true, id: undefined})}
          $isVisible={Boolean(editMode && selectingPalette && !selectingPalette.standard)}
        >
          <AddIcon />
        </MakeTemplateFab>
      </div>
      <PublishMenu menuAnchor={menuAnchor}
        onClose={() => {
          setMenuAnchor(pre => {
            if (!pre) return undefined;
            return { ...pre, open: false };
          });
          setTimeout(() => setMenuAnchor(undefined), 280);
        }}
        functions={{ publish: id => setPublishAction({ show: true, id }) }}
      />
      <EditTemplateDialog open={editTemplateAction.show} 
        onClose={() => setEditTemplateAction({show: false, id: undefined})}
        templateId={editTemplateAction.id}
        paletteId={selectingPaletteId}
        cardTemplatesLength={cardTemplatesLength}
      />
      { deleteTemplateAction.cardTemplateId !== undefined &&
      <DeleteTemplateDialog open={deleteTemplateAction.show} 
        onClose={() => setDeleteTemplateAction({show: false})}
        paletteId={deleteTemplateAction.paletteId}
        cardTemplateId={deleteTemplateAction.cardTemplateId}
        updateOrder={updateCTOrder}
      />
      }
      <AddPaletteDialog open={addPaletteAction}
        onClose={() => setAddPaletteAction(false)} />
      <DeletePaletteDialog open={deleteCategoryAction.show}
        onClose={() => setDeleteCategoryAction({show: false, id: undefined})}
        id={deleteCategoryAction.id}
        updateOrder={updatePOrder}
        updateSelection={() => {
          if (deleteCategoryAction.id) {
            const ix = paletteIds.indexOf(deleteCategoryAction.id);
            if (ix >= 1) paletteSelect(paletteIds[ix - 1]);
            else if (ix === 0) paletteSelect(paletteIds[1]);
            else paletteSelect(paletteIds[0]);
          }
        }}
      />
      <PublishDialog open={publishAction.show}
        onClose={() => setPublishAction({ show: false, id: undefined })}
        goodsKind="card_template"
        id={publishAction.id}
      />
    </DialogContent>
  );
}

type AddCardDialogProps = {
  open: boolean;
  onClose: () => void;
  parent?: SheetKinds | string;
  selectingCardValue?: CardDb;
  ids: string[];
  cards: CardDb[];
  index?: number;
  coordinate?: CoordinateType;
  grid?: CoordinateType;
  clearSelectingGrid?: () => void;
  cutInMode?: boolean;
  arrowId?: string | undefined;
}
const AddCardDialog: React.FC<AddCardDialogProps> = ({open, onClose, parent, selectingCardValue, ids, cards, index, coordinate, grid, clearSelectingGrid, cutInMode, arrowId}) => {
  const dispatch = useDispatch();
  const cardTemplateDb = useSelector((state: RootState) => state.cardTemplateDb);
  const cardTemplateAttributes = useSelector((state: RootState) => state.cardTemplateAttributeDb).cardTemplateAttributes;
  const cardTemplateShapes = useSelector((state: RootState) => state.cardTemplateShapeDb).cardTemplateShapes;
  const userId = useCurrentUserId();
  const [sheetId] = useCurrentDb();
  const [paletteIds, palettes] = useAllPaletteDbs(userId);
  const [latelyIds, , latelyCardTemplateId] = useLatelyCardTemplates();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectingPaletteId, setSelectingPaletteId] = useState<string>('lately');
  const [selectingCardId, setSelectingCardId] = useState<string | undefined>(undefined);
  const [invalidCardTransition, setInvalidCardTransition] = useState(true);
  const [invalidEditBoxTransition, setInvalidEditBoxTransition] = useState(false);

  const arrowValue = useOneArrowDb(arrowId);
  const startCard = useOneCardDb(arrowValue?.fromId) as MapCard;
  const endCard = useOneCardDb(arrowValue?.toId) as MapCard;
  const [arrowIds, arrows] = useArrowDbsByCard(arrowValue?.fromId);

  const exitDialog = () => {
    setEditMode(false);
    setSelectingCardId(undefined);
    setInvalidCardTransition(true);
  }
  const setModeEdit = (edit: boolean) => {
    setInvalidCardTransition(true);
    setTimeout(() => setInvalidCardTransition(false), 300)
    setSelectingCardId(undefined);
    setEditMode(edit);
  }
  const paletteSelect = (id: string) => {
    setInvalidCardTransition(true);
    setTimeout(() => setInvalidCardTransition(false), 300)
    setInvalidEditBoxTransition(true);
    setTimeout(() => setInvalidEditBoxTransition(false), 300)
    setSelectingPaletteId(id);
  }
  const getCardKind = (id: string): CardKinds => {
    const kind = cardTemplateDb.cardTemplates[id].kind;
    return kind;
  }
  const getCardDefaultWidth = (id: string): number => {
    const defaultWidth = cardTemplateDb.cardTemplates[id].defaultWidth;
    return defaultWidth;
  }
  const getCardDefaultHeight = (id: string): number => {
    const defaultHeight = cardTemplateDb.cardTemplates[id].defaultHeight;
    return defaultHeight;
  }
  const getCardFrame = (id: string): {display: boolean, color: string} => {
    const frame = cardTemplateDb.cardTemplates[id].frame;
    return frame;
  }
  const getCardBackground = (id: string): {display: boolean, color: string} => {
    const background = cardTemplateDb.cardTemplates[id].background;
    return background;
  }
  const getCardShadow = (id: string): boolean => {
    const shadow = cardTemplateDb.cardTemplates[id].shadow;
    return shadow;
  }
  const getCardIsGroup = (id: string) => {
    const isGroup = cardTemplateDb.cardTemplates[id].isGroup;
    return isGroup;
  }
  const getCardTemplateItems = (cardTemplateId: string): [string[], CardTemplateAttributeDb[], string[], CardTemplateShapeDb[]] => {
    const attributeIds = Object.keys(cardTemplateAttributes).filter(id => cardTemplateAttributes[id].cardTemplateId === cardTemplateId);
    const shapeIds = Object.keys(cardTemplateShapes).filter(id => cardTemplateShapes[id].cardTemplateId === cardTemplateId);
    const attributeValues = attributeIds.map(id => cardTemplateAttributes[id]);
    const shapeValues = shapeIds.map(id => cardTemplateShapes[id]);
    return [attributeIds, attributeValues, shapeIds, shapeValues];
  }
  const dicision = () => {
    if (!sheetId || selectingCardId === undefined) return;

    const newCardId = generateCardId();
    const [templateAttributeIds, templateAttributes, templateShapeIds, templateShapes] = getCardTemplateItems(selectingCardId);
    type NewCardBaseProps = CardBaseProps & { id: string }
    const newCardBase: NewCardBaseProps = 
      { id: newCardId, sheetId: sheetId, cardTemplateId: selectingCardId, kind: getCardKind(selectingCardId),
        defaultWidth: getCardDefaultWidth(selectingCardId), defaultHeight: getCardDefaultHeight(selectingCardId),
        frame: getCardFrame(selectingCardId), background: getCardBackground(selectingCardId), shadow: getCardShadow(selectingCardId),
        isGroup: getCardIsGroup(selectingCardId), groupParent: undefined
      };

    const getContent = (newCardId: string, newCard: CardAddPayload): AddCardValueProps | undefined => {
      if (isCommonCard(newCard)) {
        const items: CardAttributeDb[] = templateAttributeIds.map((id, ix) => getAttributeInitial(newCardId, id, templateAttributes[ix]));
        const shapes: CardShapeDb[] = templateShapeIds.map((id, ix) => {
          const {cardTemplateId, ...obj} = templateShapes[ix];
          return {cardId: newCardId, cardTemplateShapeId: id, ...obj};
        })
        return {dispatch: dispatch, card: newCard, items: items, shapes: shapes};
      } else if (isMemoCard(newCard)) {
        const memoContent: MemoCardContentDb = {cardId: newCardId, content: ''};
        return {dispatch: dispatch, card: newCard, memoContent: memoContent};
      } else if (isUiCard(newCard)) {
        return {dispatch: dispatch, card: newCard};
      } else if (isDrawCard(newCard)) {
        const drawContent: DrawCardContentDb = {cardId: newCardId, content: ''};
        return {dispatch: dispatch, card: newCard, drawContent: drawContent};
      }
    }

    if (parent === 'story' && index !== undefined) {
      let targetIndex = index + 1;
      for (let i = 0; i < ids.length; i++) {
        const card = cards[i];
        if (isStoryRootCard(card) && card.order === targetIndex) {
          dispatch(updateCard({id: ids[i], order: targetIndex + 1}));
          targetIndex++;
        }
      }
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'story', order: index + 1, headerText: ''};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    } else if (parent === 'story' && index === undefined) {
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'story', order: ids.length || 0, headerText: ''};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    } else if (parent === 'map') {
      let x = coordinate ? coordinate.x : 0;
      let y = coordinate ? coordinate.y : 0;
      const noGroupLength = linq.from(cards).where(card => !card.isGroup && !card.groupParent).toArray().length;
      const groupLength = linq.from(cards).where(card => card.isGroup && !card.groupParent).toArray().length;
      let z = newCardBase.isGroup ? groupLength : noGroupLength;
      if (cutInMode) {// 矢印カットインの場合は座標指定
        if (startCard && endCard) {
          x = (startCard.x + endCard.x) / 2;
          y = (startCard.y + endCard.y) / 2;
        }
      }
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'map', x: x, y: y, z: z};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
      if (cutInMode && arrowId && arrowValue) {// 矢印カットインの場合、矢印を2つに分割
        const updateArrowOrder = () => {
          if (arrowIds.length === 0) return;
          const nomineeIds: string[] = [];
          const nominees: ArrowDb[] = [];
          arrowIds.forEach((targetId, ix) => {
            const value = arrows[ix];
            if (value.fromId === arrowValue.toId || value.toId === arrowValue.toId) {
              nomineeIds.push(targetId);
              nominees.push(value);
            }
          })
          if (nomineeIds.length === 0) return;
          nomineeIds.forEach((targetId, ix) => {
            const value = nominees[ix];
            if (value.order > arrowValue.order) dispatch(updateArrow({ id: targetId, order: value.order - 1 }));
          })
        }
        updateArrowOrder();
        dispatch(deleteArrow({ id: arrowId }));
        dispatch(addArrow({ ...initial.arrow,
                            sheetId: sheetId, fromId: arrowValue.fromId, toId: newCardId,
                            thickness: arrowValue.thickness, color: arrowValue.color, lineStyle: arrowValue.lineStyle, return: arrowValue.return,
                            shapes: arrowValue.shapes, labels: arrowValue.labels, imageId: arrowValue.imageId, notes: arrowValue.notes
                          }));
        dispatch(addArrow({ ...initial.arrow,
                            sheetId: sheetId, fromId: newCardId, toId: arrowValue.toId,
                            thickness: arrowValue.thickness, color: arrowValue.color, lineStyle: arrowValue.lineStyle, return: arrowValue.return,
                            shapes: arrowValue.shapes, labels: arrowValue.labels, imageId: arrowValue.imageId, notes: arrowValue.notes
                          }));
      }
    } else if (parent === 'grid' && grid) {
      const newCard: CardAddPayload = {...newCardBase, sheetKind: 'grid', x: grid.x * 220, y: grid.y * 144, column: grid.x, row: grid.y};
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
      if (clearSelectingGrid) clearSelectingGrid();
    } else if (parent && isStoryCard(selectingCardValue)) {
      let brotherIds: string[] = [];
      let brotherValues: StoryCard[] = [];
      ids.forEach((id, ix) => {
        const card = cards[ix];
        if (isStoryCard(card) && card.parentId === parent) {
          brotherIds.push(id);
          brotherValues.push(card);
        }
      })
      const newCard: CardAddPayload = { ...newCardBase, sheetKind: 'story', parentId: parent, order: 0, headerText: '' };
      if (selectingCardValue.parentId === parent) {
        newCard.order = selectingCardValue.order + 1;
        brotherIds.forEach((id, ix) => {
          const card = brotherValues[ix];
          if (card.order > selectingCardValue.order) dispatch(updateCard({ id, order: card.order + 1 }));
        })
      } else {
        brotherIds.forEach((id, ix) => dispatch(updateCard({ id, order: brotherValues[ix].order + 1 })))
      }
      const cardContent = getContent(newCardId, newCard);
      addCardValue(cardContent);
    }

    const newLatelyIds = makeLately(latelyIds, selectingCardId, 'add');
    dispatch(updateLatelyCardTemplate({id: latelyCardTemplateId, cardTemplateIds: newLatelyIds}));

    if (onClose) onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} onExited={exitDialog} maxWidth="xl" fullWidth
      PaperProps={{ style: { overflow: 'hidden' } }}
    >
      <FadeDialogTitle $isVisible={!editMode}>カードをシートに追加します</FadeDialogTitle>
      <FadeDialogTitle $isVisible={editMode} style={{position: 'absolute'}}>選択したカードを編集します</FadeDialogTitle>
      { (!cutInMode) &&
        <EditButton color='primary' onClick={() => setModeEdit(!editMode)} size="large">{editMode ? '完了' : '編集'}</EditButton> 
      }
      <AddCardDialogContent open={open} editMode={editMode}
        paletteIds={paletteIds} palettes={palettes}
        selectingPaletteId={selectingPaletteId} paletteSelect={paletteSelect}
        selectingCardId={selectingCardId} setSelectingCardId={setSelectingCardId}
        getCardKind={getCardKind} getCardTemplateItems={getCardTemplateItems}
        dicision={dicision}
        invalidCardTransition={invalidCardTransition}
        setInvalidCardTransition={setInvalidCardTransition}
        invalidEditBoxTransition={invalidEditBoxTransition}
      />
      <AddCardDialogActions $isVisible={!editMode}>
        <Button color='primary' onClick={onClose} size="large">
          キャンセル
        </Button>
        <Button color='primary' onClick={() => dicision()} size="large"
          disabled={selectingCardId === undefined}>
          選択したカードをシートに追加する
        </Button>
      </AddCardDialogActions>
    </Dialog>
  );
}

export default AddCardDialog;
